import { AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiService } from "./api.service";
import { FLOW_VAL_PREAUTORIZADO, SUSCRIPTION_KEY } from "@/constants/config";
import { ConsultaCliente } from "@/models/requests/lead.interface";
import { ConsultaNC, ConsultaPreautorizado, OfertaPreautorizada } from "@/models/requests/preautorizados.interface";
import { LeadPrincipalImssRequest } from "@/models/requests/imss.interface";
import { ODataBaseResponse, ODataMultipleResponse } from "@/models/responses/odata.response.interface";
import { LogCalculatorResponse } from "@/models/responses/preautorizados.interface";

interface LeadImssHeadODataResponse extends ODataBaseResponse {
  opportunityid?: string | PromiseLike<string>;
  leadid: string;
  rs_folio: string;
  firstname: string;
  rs_segundonombre?: string;
  middlename: string;
  lastname: string;
  rs_nipenviado: string;
  rs_codigoverificador: string;
  rs_fechanacimiento: string;
  rs_tipocliente: number;
  rs_numcredito: string;
  rs_origenprospecto: number;
  rs_buroautenticado: number;
  rs_bcautenticado: boolean;
  mobilephone: string;
  emailaddress1: string;
  rs_edad: number;
  rs_validarmediocontacto: number;
  rs_tipoproyecto: number;
  rs_matricula: string;
  rs_estadodelegacionid: number;
  rs_finalidaddelosrecursos: number;
}

interface OfferPreODataResponse {
  rs_tipocampania?: number;
  rs_ofertapreautorizadaid?: string;
  statecode?: number;
}

interface LogCalculatorResponseODataResponse {
  rs_response: string;
}

class PreautorizadoService extends ApiService {
  public constructor(config?: AxiosRequestConfig) {
    super(config);

    this.createPreautorizado = this.createPreautorizado.bind(this);
  }

  /**
   * Returns a create preautorizado.
   *
   * @param {string} guid
   * @returns {Promise<String>}
   * @memberof PreautorizadoService
   */
  public async createPreautorizado(guid: Readonly<string>): Promise<string> {
    const res = await this.post<{ leadid: string }, unknown, AxiosResponse<{ leadid: string }>>(
      `Flows/v1/workflows/${FLOW_VAL_PREAUTORIZADO}/triggers/manual/paths/invoke`,
      {
        guid: guid
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    //respuesta flow
    return res.data.leadid;
  }

  public async consultaPreautorizado(request: any): Promise<ConsultaPreautorizado> {
    const res = await this.post<ConsultaPreautorizado, unknown, AxiosResponse<ConsultaPreautorizado>>(`flows-portal/v1/consulta-preautorizado`, request, {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });

    const result: ConsultaPreautorizado = {
      preautorizado: res.data.preautorizado,
      code: res.data.code,
      message: res.data.message
    };
    return result;
  }

  public async consultaNumeroCliente(request: any): Promise<ConsultaNC> {
    const res = await this.post<ConsultaNC, unknown, AxiosResponse<ConsultaNC>>(`flows-portal/v1/consulta-numero-cliente`, request, {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });

    const result: ConsultaNC = {
      code: res.data.code,
      numeroCliente: res.data.numeroCliente
    };
    return result;
  }

  public async createPreauOppo(request: Readonly<LeadPrincipalImssRequest>): Promise<string> {
    const res = await this.post<LeadImssHeadODataResponse, unknown, AxiosResponse<LeadImssHeadODataResponse>>(
      "d365api/v1/opportunities",
      {
        /* rs_rfc: request.rfc,
        rs_curp: request.curp, */
        rs_numerocliente: request.clientNumber,
        "parentcontactid@odata.bind": `/contacts(${request.contactId})`,
        /*"rs_finalidad@odata.bind": `/rs_finalidads(${request.purposeId})`,*/
        "ownerid@odata.bind": `/systemusers(${request.ownerId})`,
        "rs_usuarioportalcallcenterid@odata.bind": `/rs_usuarioportalcallcenters(${request.userId})`,
        "rs_sucursalid@odata.bind": `/rs_sucursals(${request.branchId})`,
        rs_nombres: request.firstName,
        rs_segundonombre: request.middleName,
        rs_apellidopaterno: request.lastName,
        rs_apellidomaterno: request.mothersLastName,
        rs_fechanacimiento: request.birthDate,
        rs_correoelectronico: request.email,
        rs_telfonocelular: request.mobile,
        rs_tipoproceso: 2, //Actualizacion
        rs_tipocliente: 1, //
        rs_origen: 4, //CALL CENTER //request.origin,
        rs_validarmediocontacto: request.contactMean,
        rs_tipoproyecto: 1, //ORIGINACION
        rs_destinorecursos: request.purposeResources,
        rs_instrumentomonetariomanejaracuentas: request.monetaryInstrument,
        rs_tipofolio: 717810003, //Preautorizado
        "rs_registradapor@odata.bind": `/systemusers(${request.ownerId})`
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    return res.data.opportunityid;
  }

  public async findOfferPre(id: string): Promise<OfferPreODataResponse> {
    const res = await this.get<OfferPreODataResponse, AxiosResponse<ODataMultipleResponse<OfferPreODataResponse[]>>>(
      `d365api/v1/rs_ofertapreautorizadas?$select=rs_ofertapreautorizadaid,rs_tipocampania,statecode&$filter=(rs_tipocampania eq 717810000 or rs_tipocampania eq 717810001 or rs_tipocampania eq 717810002 or rs_tipocampania eq 717810003) and (_rs_clienteid_value eq ${id})&$orderby=createdon desc`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );

    if (res.data?.value != null && res.data.value.length > 0) {
      const data: OfferPreODataResponse = res.data.value[0];
      return {
        rs_ofertapreautorizadaid: data.rs_ofertapreautorizadaid,
        rs_tipocampania: data.rs_tipocampania,
        statecode: data.statecode
      };
    }
    return null;
  }

  public async consultaOfertaPreautorizado(numeroCliente: any): Promise<OfertaPreautorizada> {
    const res = await this.get<OfertaPreautorizada, AxiosResponse<OfertaPreautorizada>>(`functions-simplificacion/v1/ConsultarClientePreautorizado?numerocliente=${numeroCliente}`, {
      headers: {
        "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
      }
    });

    const result: OfertaPreautorizada = {
      Code: res.data.Code,
      Mensaje: res.data.Mensaje,
      Oferta: res.data.Oferta
    };
    return result;
  }

  public async findCalculator(id: string): Promise<LogCalculatorResponse> {
    const res = await this.get<LogCalculatorResponse, AxiosResponse<ODataMultipleResponse<LogCalculatorResponseODataResponse[]>>>(
      `d365api/v1/rs_logservicioses?$select=rs_response&$filter=(rs_tiposervicio eq 717810045 and _rs_oportunidadid_value eq ${id})`,
      {
        headers: {
          "Ocp-Apim-Subscription-Key": SUSCRIPTION_KEY
        }
      }
    );
    if (res.data?.value != null) {
      return {
        response: res?.data?.value[0]?.rs_response
      };
    } else {
      return {
        response: null
      };
    }
  }
}

export const preautorizadoService = new PreautorizadoService();
